import useScreen from "Hooks/useScreen";
import { TeamPricesData } from "modules/Types";
import { ReactComponent as UserIcon } from "assets/images/user.svg"
import { useStyles } from "./AddUserModal/styles";
import { Divider } from "@material-ui/core";
import Banner from "elements/Banner";
import gravatar from "gravatar";
import Avatar from '@mui/material/Avatar';
import { Body2, Subtitle1 } from "components/shareable/Typography";
import BrandsList from "./BrandsList/BrandsList";
import { TEAM_PLAN_INCLUDED_SEATS } from "modules/Const"
import useOrganization from 'Hooks/useOrganization';
import Colors from "modules/Colors";
import ButtonSymphony from "elements/ButtonSymphony";
import ModalSymphony from "elements/ModalSymphony";
import { clsx } from "modules/Utils";
import { toastError, toastSuccess } from "modules/Toast";

interface RemoveMemberModalProps {
  className: string;
  isOpen: boolean;
  onClose: () => void;
  teamPrices?: TeamPricesData;
}

const RemoveMemberModal = ({
  className,
  isOpen,
  onClose,
  teamPrices,
}: RemoveMemberModalProps) => {
  const classes = useStyles()
  const { mobileView } = useScreen();
  const { teamMembers, removeOrganizationTeamMember, isLoadingTeamMembers, getSelectedTeamMember } = useOrganization();
  const selectedTeamMember = getSelectedTeamMember();
  const memberCountAfterUpdate = teamMembers.length - 1; // NOTE: this is a prview sayin "hey, after this, you will have X number of seats left"

  if (!selectedTeamMember) {
    return null;
  }

  const onConfirmDeletion = async () => {
    try {
      await removeOrganizationTeamMember(selectedTeamMember.id);
      toastSuccess("Your team member has been removed.")
      onClose();
    } catch (error) {
      toastError("Something went wrong removing your team member.")
      console.error(error);
    }
  };


  return (
    <ModalSymphony
      className={clsx('removeMemberModalComponent', className)}
      isOpen={isOpen}
      onClose={() => {
        if (isLoadingTeamMembers) {
          return
        }
        onClose()
      }}
      fullScreen={mobileView}
      title="Remove member"
      subtitle="Are you sure you want to remove this member?"
      headerIcon={<UserIcon width={32} height={32} className={classes.userIcon} />}
    >
      <div className="flex flex-col gap-8 p-">
        <div className="flex gap-4 items-center">
          <Avatar src={gravatar.url(selectedTeamMember.email, { protocol: 'https', d: 'retro' })} sx={{ width: 32, height: 32 }} />
          <div className="flex flex-col gap-1">
            <Body2 color={Colors.textColor}>{selectedTeamMember.name ? `${selectedTeamMember.name} ${selectedTeamMember?.lastName || ''}` : selectedTeamMember.email}</Body2>
            {selectedTeamMember.name && <Body2>{selectedTeamMember.email}</Body2>}
          </div>
        </div>
        <Divider />
        <div>
          <Body2 color={Colors.textColor}>Linked to:</Body2>
          {/* TODO XXX: check 'as any' this is the same TeamMember.brands vs CurrentBrand[] issue */}
          <BrandsList brands={selectedTeamMember.brands as any} canEdit={false} />
        </div>
        {memberCountAfterUpdate < TEAM_PLAN_INCLUDED_SEATS ?
          <Banner className="removeMemberModalBanner1" color={Colors.purpleLight} style={{ marginBottom: 16 }}>
            <Subtitle1>This action will free up a member space</Subtitle1>
            <Body2>Removing this member will free up 1 of your {TEAM_PLAN_INCLUDED_SEATS} spaces, so you'll be able to invite {TEAM_PLAN_INCLUDED_SEATS - (teamMembers.length - 1)} more members without additional fees.</Body2>
          </Banner>
          :
          <Banner className="removeMemberModalBanner2" color={Colors.purpleLight} style={{ marginBottom: 16 }}>
            <Subtitle1>This action will reduce your monthly subscription</Subtitle1>
            <Body2>Removing this extra member will reduce - ${teamPrices?.priceSeatRemaining || 0} USD in your next invoice.</Body2>
          </Banner>
        }
        <div className="flex w-full justify-end gap-2">
          <ButtonSymphony
            className="buttonCancelRemoveMember"
            variant='outlined'
            onClick={onClose}
            disabled={isLoadingTeamMembers}
          >
            Cancel
          </ButtonSymphony>
          <ButtonSymphony
            className="buttonYesRemoveMember"
            onClick={onConfirmDeletion}
            disabled={isLoadingTeamMembers}
            isLoading={isLoadingTeamMembers}
            color="red"
            variant="outlined"
          >
            Yes, remove member
          </ButtonSymphony>
        </div>
      </div>
    </ModalSymphony>
  );
};

export default RemoveMemberModal;